import {NumberInputTypes, REGEXP_MAP} from '../constants';

import type {NumberInputType} from '../typedef';

const doubleZeroCheck = (enteredValue: string): boolean =>
    enteredValue.startsWith('00');

const minusZeroCheck = (enteredValue: string): boolean =>
    enteredValue.includes('-0');

const endsWithMinusCheck = (enteredValue: string): boolean =>
    enteredValue.endsWith('-') && enteredValue.length > 1;

const startsWithDotCheck = (enteredValue: string): boolean =>
    enteredValue.startsWith('.');

const minusDotCheck = (enteredValue: string): boolean =>
    enteredValue.includes('-.');

const multipleDotsCheck = (enteredValue: string): boolean =>
    [...enteredValue].filter(char => char === '.').length > 1;

const minPositiveIntegerCheck = (enteredValue: string, minValue: number): boolean =>
    parseInt(enteredValue, 10) < minValue;

const maxPositiveIntegerCheck = (enteredValue: string, maxValue: number): boolean =>
    parseInt(enteredValue, 10) > maxValue;

const minIntegerCheck = (enteredValue: string, minValue: number): boolean => {
    return !enteredValue.endsWith('-') && parseInt(enteredValue, 10) < minValue;
};

const maxIntegerCheck = (enteredValue: string, maxValue: number): boolean => {
    return !enteredValue.endsWith('-') && parseInt(enteredValue, 10) > maxValue;
};

const minPositiveFloatCheck = (enteredValue: string, minValue: number): boolean => {
    return !enteredValue.endsWith('.') && parseFloat(enteredValue) < minValue;
};

const maxPositiveFloatCheck = (enteredValue: string, maxValue: number): boolean => {
    return !enteredValue.endsWith('.') && parseFloat(enteredValue) > maxValue;
};

const minFloatCheck = (enteredValue: string, minValue: number): boolean => {
    return !enteredValue.endsWith('-') && !enteredValue.endsWith('.') && parseFloat(enteredValue) < minValue;
};

const maxFloatCheck = (enteredValue: string, maxValue: number): boolean => {
    return !enteredValue.endsWith('-') && !enteredValue.endsWith('.') && parseFloat(enteredValue) > maxValue;
};

export type ValidateInternalStateProps = {
    enteredValue: string,
    inputType: NumberInputType,
    minValue?: number,
    maxValue?: number,
};

export const validateInternalState = ({
    enteredValue,
    inputType,
    minValue,
    maxValue,
}: ValidateInternalStateProps): boolean => {
    let invalid = false;

    const regex = REGEXP_MAP[inputType];
    if (!regex.test(enteredValue)) {
        invalid = true;
    }

    const positiveIntegerChecks = [
        doubleZeroCheck,
    ];

    const integerChecks = [
        doubleZeroCheck,
        minusZeroCheck,
        endsWithMinusCheck,
    ];

    const positiveFloatChecks = [
        doubleZeroCheck,
        startsWithDotCheck,
        multipleDotsCheck,
    ];

    const floatChecks = [
        doubleZeroCheck,
        startsWithDotCheck,
        multipleDotsCheck,
        minusZeroCheck,
        minusDotCheck,
        endsWithMinusCheck,
    ];

    switch (inputType) {
        case NumberInputTypes.POSITIVE_INTEGER:
            if ([
                positiveIntegerChecks.some(check => check(enteredValue)),
                minValue ? minPositiveIntegerCheck(enteredValue, minValue) : false,
                maxValue ? maxPositiveIntegerCheck(enteredValue, maxValue) : false,
            ].some(Boolean)) {
                invalid = true;
            }
            break;

        case NumberInputTypes.INTEGER:
            if ([
                integerChecks.some(check => check(enteredValue)),
                minValue ? minIntegerCheck(enteredValue, minValue) : false,
                maxValue ? maxIntegerCheck(enteredValue, maxValue) : false,
            ].some(Boolean)) {
                invalid = true;
            }
            break;

        case NumberInputTypes.POSITIVE_FLOAT:
            if ([
                positiveFloatChecks.some(check => check(enteredValue)),
                minValue ? minPositiveFloatCheck(enteredValue, minValue) : false,
                maxValue ? maxPositiveFloatCheck(enteredValue, maxValue) : false,
            ].some(Boolean)) {
                invalid = true;
            }
            break;

        case NumberInputTypes.FLOAT:
            if ([
                floatChecks.some(check => check(enteredValue)),
                minValue ? minFloatCheck(enteredValue, minValue) : false,
                maxValue ? maxFloatCheck(enteredValue, maxValue) : false,
            ].some(Boolean)) {
                invalid = true;
            }
            break;
    }

    return !invalid;
};
